import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { affiliatesApi, invitesApi } from '@/lib/api';
import { Affiliate } from '@/types';
import { format } from 'date-fns';
import { toast } from 'react-hot-toast';
import clsx from 'clsx';
import { useAuthStore } from '@/stores/auth';
import {
  CheckIcon,
  XMarkIcon,
  NoSymbolIcon,
  PlusIcon,
  ClipboardIcon,
  EnvelopeIcon,
  LinkIcon,
  TrashIcon,
  ShareIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/outline';

interface AffiliateInvite {
  inviteId: string;
  inviteCode: string;
  inviteUrl: string;
  email?: string;
  status: string;
  expiresAt: string;
  createdAt: string;
}

function formatCurrency(cents: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(cents / 100);
}

const statusStyles: Record<string, string> = {
  pending: 'badge-warning',
  approved: 'badge-success',
  suspended: 'badge-danger',
  rejected: 'badge-neutral',
};

export default function OrgAffiliates() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [affiliates, setAffiliates] = useState<Affiliate[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filter, setFilter] = useState<string>('all');
  const [activeTab, setActiveTab] = useState<'affiliates' | 'invites'>('affiliates');
  const { currentOrganization } = useAuthStore();

  // Compute signup URL
  const baseUrl = window.location.origin;
  const signupUrl = currentOrganization?.slug
    ? `${baseUrl}/register?org=${currentOrganization.slug}`
    : null;

  // Invite modal state - auto-open if ?invite=true in URL
  const [showInviteModal, setShowInviteModal] = useState(searchParams.get('invite') === 'true');

  // Clear the query param after opening modal
  useEffect(() => {
    if (searchParams.get('invite') === 'true') {
      setSearchParams({}, { replace: true });
    }
  }, []);
  const [inviteType, setInviteType] = useState<'link' | 'email'>('link');
  const [inviteEmail, setInviteEmail] = useState('');
  const [isCreatingInvite, setIsCreatingInvite] = useState(false);
  const [createdInvite, setCreatedInvite] = useState<{ inviteUrl: string; inviteCode: string } | null>(null);

  // Invites list state
  const [invites, setInvites] = useState<AffiliateInvite[]>([]);
  const [isLoadingInvites, setIsLoadingInvites] = useState(false);

  const fetchInvites = async () => {
    setIsLoadingInvites(true);
    try {
      const response = await invitesApi.list();
      if (response.data.success) {
        setInvites(response.data.data.invites);
      }
    } catch (error) {
      console.error('Failed to fetch invites:', error);
    } finally {
      setIsLoadingInvites(false);
    }
  };

  const fetchAffiliates = async () => {
    setIsLoading(true);
    try {
      const params: { status?: string } = {};
      if (filter !== 'all') {
        params.status = filter;
      }
      const response = await affiliatesApi.list(params);
      if (response.data.success) {
        setAffiliates(response.data.data.affiliates);
      }
    } catch (error) {
      console.error('Failed to fetch affiliates:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchAffiliates();
  }, [filter]);

  useEffect(() => {
    if (activeTab === 'invites') {
      fetchInvites();
    }
  }, [activeTab]);

  const handleCreateInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsCreatingInvite(true);

    try {
      const data: { email?: string } = {};
      if (inviteType === 'email' && inviteEmail) {
        data.email = inviteEmail;
      }

      const response = await invitesApi.create(data);
      if (response.data.success) {
        const { inviteUrl, inviteCode } = response.data.data.invite;
        setCreatedInvite({ inviteUrl, inviteCode });
        if (inviteType === 'email') {
          toast.success('Invitation email sent!');
        } else {
          toast.success('Invite link created!');
        }
        fetchInvites();
      }
    } catch (error: unknown) {
      const err = error as { response?: { data?: { error?: { message?: string } } } };
      toast.error(err.response?.data?.error?.message || 'Failed to create invite');
    } finally {
      setIsCreatingInvite(false);
    }
  };

  const handleCopyLink = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Link copied to clipboard!');
    } catch {
      toast.error('Failed to copy link');
    }
  };

  const handleRevokeInvite = async (inviteId: string) => {
    if (!confirm('Are you sure you want to revoke this invite?')) return;

    try {
      await invitesApi.revoke(inviteId);
      toast.success('Invite revoked');
      fetchInvites();
    } catch {
      toast.error('Failed to revoke invite');
    }
  };

  const resetInviteModal = () => {
    setShowInviteModal(false);
    setInviteType('link');
    setInviteEmail('');
    setCreatedInvite(null);
  };

  const handleApprove = async (affiliateId: string) => {
    try {
      await affiliatesApi.approve(affiliateId);
      toast.success('Affiliate approved');
      fetchAffiliates();
    } catch {
      toast.error('Failed to approve affiliate');
    }
  };

  const handleReject = async (affiliateId: string) => {
    const reason = prompt('Reason for rejection (optional):');
    try {
      await affiliatesApi.reject(affiliateId, reason || undefined);
      toast.success('Affiliate rejected');
      fetchAffiliates();
    } catch {
      toast.error('Failed to reject affiliate');
    }
  };

  const handleSuspend = async (affiliateId: string) => {
    const reason = prompt('Reason for suspension (optional):');
    try {
      await affiliatesApi.suspend(affiliateId, reason || undefined);
      toast.success('Affiliate suspended');
      fetchAffiliates();
    } catch {
      toast.error('Failed to suspend affiliate');
    }
  };

  const inviteStatusStyles: Record<string, string> = {
    pending: 'badge-warning',
    accepted: 'badge-success',
    expired: 'badge-neutral',
    revoked: 'badge-danger',
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-display font-bold text-charcoal">Affiliates</h2>
          <p className="text-charcoal/60 mt-1">Manage your affiliate partners</p>
        </div>
        <button onClick={() => setShowInviteModal(true)} className="btn btn-primary">
          <PlusIcon className="w-5 h-5 mr-2" />
          Invite Affiliate
        </button>
      </div>

      {/* Public Signup URL Section */}
      {signupUrl && (
        <div className="rounded-xl bg-warm-white shadow-sm p-5">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-lg bg-sage/10 flex items-center justify-center flex-shrink-0">
              <ShareIcon className="w-5 h-5 text-sage" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-medium text-charcoal mb-1">Public Affiliate Signup URL</h3>
              <p className="text-sm text-charcoal/60 mb-3">
                Share this link on your website, social media, or anywhere to let potential affiliates sign up directly.
              </p>
              <div className="flex items-center gap-2">
                <div className="flex-1 bg-cream rounded-lg px-3 py-2 overflow-hidden">
                  <code className="text-sm text-rust break-all">{signupUrl}</code>
                </div>
                <button
                  onClick={() => handleCopyLink(signupUrl)}
                  className="btn btn-secondary flex-shrink-0"
                  title="Copy signup URL"
                >
                  <ClipboardIcon className="w-4 h-4 mr-2" />
                  Copy
                </button>
              </div>
              <div className="flex items-center gap-1.5 text-xs text-charcoal/50 mt-3 pt-3 border-t border-tan/20">
                <InformationCircleIcon className="w-4 h-4" />
                <span>
                  {currentOrganization?.settings?.autoApproveAffiliates
                    ? 'Affiliates who sign up will be automatically approved.'
                    : 'Affiliates who sign up will need your approval before they can start.'}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-4 border-b border-tan/30">
        <button
          onClick={() => setActiveTab('affiliates')}
          className={clsx(
            'pb-3 px-1 text-sm font-medium transition-colors border-b-2',
            activeTab === 'affiliates'
              ? 'border-rust text-rust'
              : 'border-transparent text-charcoal/60 hover:text-charcoal'
          )}
        >
          Affiliates
        </button>
        <button
          onClick={() => setActiveTab('invites')}
          className={clsx(
            'pb-3 px-1 text-sm font-medium transition-colors border-b-2',
            activeTab === 'invites'
              ? 'border-rust text-rust'
              : 'border-transparent text-charcoal/60 hover:text-charcoal'
          )}
        >
          Pending Invites
        </button>
      </div>

      {activeTab === 'affiliates' && (
        <>
          {/* Filter */}
          <div className="flex gap-2">
            {['all', 'pending', 'approved', 'suspended', 'rejected'].map((status) => (
              <button
                key={status}
                onClick={() => setFilter(status)}
                className={clsx(
                  'px-4 py-2 rounded-lg text-sm font-medium transition-colors',
                  filter === status
                    ? 'bg-rust text-white'
                    : 'bg-warm-white text-charcoal/70 hover:bg-cream'
                )}
              >
                {status.charAt(0).toUpperCase() + status.slice(1)}
              </button>
            ))}
          </div>

          {/* Table */}
          <div className="rounded-xl bg-warm-white shadow-sm overflow-hidden">
            {isLoading ? (
              <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-rust"></div>
              </div>
            ) : affiliates.length === 0 ? (
              <div className="p-12 text-center">
                <p className="text-charcoal/60">No affiliates found</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-cream/50 border-b border-tan/30">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-charcoal uppercase tracking-wider">Affiliate</th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-charcoal uppercase tracking-wider">Referral Code</th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-charcoal uppercase tracking-wider">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-charcoal uppercase tracking-wider">Earnings</th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-charcoal uppercase tracking-wider">Conversions</th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-charcoal uppercase tracking-wider">Joined</th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-charcoal uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-tan/20">
                    {affiliates.map((affiliate) => (
                      <tr key={affiliate.affiliateId} className="hover:bg-cream/30">
                        <td className="px-6 py-4">
                          <div>
                            <p className="font-medium text-charcoal">
                              {affiliate.name || `Affiliate #${affiliate.affiliateId.slice(0, 8)}`}
                            </p>
                            {affiliate.email && (
                              <p className="text-sm text-charcoal/60">{affiliate.email}</p>
                            )}
                            {affiliate.applicationNote && (
                              <p className="text-xs text-charcoal/50 mt-1 truncate max-w-xs" title={affiliate.applicationNote}>
                                {affiliate.applicationNote}
                              </p>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <code className="text-sm text-rust bg-rust/10 px-2 py-1 rounded">
                            {affiliate.referralCode}
                          </code>
                        </td>
                        <td className="px-6 py-4">
                          <span className={clsx('badge', statusStyles[affiliate.status])}>
                            {affiliate.status}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <span className="font-serif font-medium text-charcoal">
                            {formatCurrency(affiliate.totalEarnings)}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-charcoal/70">{affiliate.totalConversions}</td>
                        <td className="px-6 py-4 text-charcoal/60">
                          {format(new Date(affiliate.createdAt), 'MMM d, yyyy')}
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            {affiliate.status === 'pending' && (
                              <>
                                <button
                                  onClick={() => handleApprove(affiliate.affiliateId)}
                                  className="p-2 text-sage hover:bg-sage/10 rounded-lg"
                                  title="Approve"
                                >
                                  <CheckIcon className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() => handleReject(affiliate.affiliateId)}
                                  className="p-2 text-rust hover:bg-rust/10 rounded-lg"
                                  title="Reject"
                                >
                                  <XMarkIcon className="w-4 h-4" />
                                </button>
                              </>
                            )}
                            {affiliate.status === 'approved' && (
                              <button
                                onClick={() => handleSuspend(affiliate.affiliateId)}
                                className="p-2 text-tan hover:bg-tan/20 rounded-lg"
                                title="Suspend"
                              >
                                <NoSymbolIcon className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {activeTab === 'invites' && (
        <div className="rounded-xl bg-warm-white shadow-sm overflow-hidden">
          {isLoadingInvites ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-rust"></div>
            </div>
          ) : invites.length === 0 ? (
            <div className="p-12 text-center">
              <div className="w-16 h-16 bg-cream rounded-full flex items-center justify-center mx-auto mb-4">
                <EnvelopeIcon className="w-8 h-8 text-charcoal/40" />
              </div>
              <h3 className="text-lg font-medium text-charcoal mb-2">No pending invites</h3>
              <p className="text-charcoal/60 mb-6">Create invite links or send email invitations to affiliates.</p>
              <button onClick={() => setShowInviteModal(true)} className="btn btn-primary">
                <PlusIcon className="w-5 h-5 mr-2" />
                Invite Affiliate
              </button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-cream/50 border-b border-tan/30">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-charcoal uppercase tracking-wider">Type</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-charcoal uppercase tracking-wider">Email / Link</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-charcoal uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-charcoal uppercase tracking-wider">Expires</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-charcoal uppercase tracking-wider">Created</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-charcoal uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-tan/20">
                  {invites.map((invite) => (
                    <tr key={invite.inviteId} className="hover:bg-cream/30">
                      <td className="px-6 py-4">
                        {invite.email ? (
                          <div className="flex items-center gap-2">
                            <EnvelopeIcon className="w-4 h-4 text-charcoal/60" />
                            <span className="text-sm text-charcoal">Email</span>
                          </div>
                        ) : (
                          <div className="flex items-center gap-2">
                            <LinkIcon className="w-4 h-4 text-charcoal/60" />
                            <span className="text-sm text-charcoal">Link</span>
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        {invite.email ? (
                          <span className="text-charcoal">{invite.email}</span>
                        ) : (
                          <div className="flex items-center gap-2">
                            <code className="text-xs text-rust bg-rust/10 px-2 py-1 rounded truncate max-w-[200px]">
                              {invite.inviteUrl}
                            </code>
                            <button
                              onClick={() => handleCopyLink(invite.inviteUrl)}
                              className="p-1 text-charcoal/60 hover:text-charcoal"
                              title="Copy link"
                            >
                              <ClipboardIcon className="w-4 h-4" />
                            </button>
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <span className={clsx('badge', inviteStatusStyles[invite.status])}>
                          {invite.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-charcoal/60">
                        {format(new Date(invite.expiresAt), 'MMM d, yyyy')}
                      </td>
                      <td className="px-6 py-4 text-charcoal/60">
                        {format(new Date(invite.createdAt), 'MMM d, yyyy')}
                      </td>
                      <td className="px-6 py-4">
                        {invite.status === 'pending' && (
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => handleCopyLink(invite.inviteUrl)}
                              className="p-2 text-charcoal/60 hover:text-charcoal hover:bg-cream rounded-lg"
                              title="Copy link"
                            >
                              <ClipboardIcon className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleRevokeInvite(invite.inviteId)}
                              className="p-2 text-charcoal/60 hover:text-rust hover:bg-rust/10 rounded-lg"
                              title="Revoke invite"
                            >
                              <TrashIcon className="w-4 h-4" />
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Invite Modal */}
      {showInviteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-charcoal/60">
          <div className="rounded-xl bg-warm-white shadow-xl w-full max-w-md animate-fade-in">
            <div className="px-6 py-4 border-b border-tan/30 flex items-center justify-between">
              <h3 className="text-lg font-display font-semibold text-charcoal">Invite Affiliate</h3>
              <button onClick={resetInviteModal} className="text-charcoal/60 hover:text-charcoal text-xl">
                &times;
              </button>
            </div>

            {createdInvite ? (
              <div className="p-6 space-y-4">
                <div className="text-center">
                  <div className="w-12 h-12 bg-sage/20 rounded-full flex items-center justify-center mx-auto mb-3">
                    <CheckIcon className="w-6 h-6 text-sage" />
                  </div>
                  <h4 className="text-lg font-medium text-charcoal mb-2">
                    {inviteType === 'email' ? 'Invitation Sent!' : 'Invite Link Created!'}
                  </h4>
                  <p className="text-charcoal/60 text-sm">
                    {inviteType === 'email'
                      ? 'The affiliate will receive an email with instructions to join.'
                      : 'Share this link with the affiliate to invite them.'}
                  </p>
                </div>

                <div className="bg-cream rounded-lg p-4">
                  <label className="block text-xs font-medium text-charcoal/60 mb-2">Invite Link</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      value={createdInvite.inviteUrl}
                      readOnly
                      className="input text-sm flex-1"
                    />
                    <button
                      onClick={() => handleCopyLink(createdInvite.inviteUrl)}
                      className="btn btn-secondary"
                    >
                      <ClipboardIcon className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                <button onClick={resetInviteModal} className="btn btn-primary w-full">
                  Done
                </button>
              </div>
            ) : (
              <form onSubmit={handleCreateInvite} className="p-6 space-y-4">
                {/* Invite Type Toggle */}
                <div>
                  <label className="block text-sm font-medium text-charcoal mb-2">Invite Method</label>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => setInviteType('link')}
                      className={clsx(
                        'flex-1 py-3 px-4 rounded-lg border-2 transition-colors flex items-center justify-center gap-2',
                        inviteType === 'link'
                          ? 'border-rust bg-rust/5 text-rust'
                          : 'border-tan/30 text-charcoal/60 hover:border-tan'
                      )}
                    >
                      <LinkIcon className="w-5 h-5" />
                      <span className="font-medium">Shareable Link</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => setInviteType('email')}
                      className={clsx(
                        'flex-1 py-3 px-4 rounded-lg border-2 transition-colors flex items-center justify-center gap-2',
                        inviteType === 'email'
                          ? 'border-rust bg-rust/5 text-rust'
                          : 'border-tan/30 text-charcoal/60 hover:border-tan'
                      )}
                    >
                      <EnvelopeIcon className="w-5 h-5" />
                      <span className="font-medium">Email Invite</span>
                    </button>
                  </div>
                </div>

                {inviteType === 'link' && (
                  <div className="bg-cream/50 rounded-lg p-4">
                    <p className="text-sm text-charcoal/70">
                      Generate a unique signup link that you can share via email, social media, or any other channel.
                      Anyone with this link can sign up as an affiliate for your program.
                    </p>
                  </div>
                )}

                {inviteType === 'email' && (
                  <div>
                    <label className="block text-sm font-medium text-charcoal mb-1">Email Address *</label>
                    <input
                      type="email"
                      value={inviteEmail}
                      onChange={(e) => setInviteEmail(e.target.value)}
                      className="input"
                      placeholder="affiliate@example.com"
                      required
                    />
                    <p className="text-xs text-charcoal/60 mt-1">
                      We&apos;ll send an invitation email with a signup link.
                    </p>
                  </div>
                )}

                <div className="flex gap-3 pt-4">
                  <button type="button" onClick={resetInviteModal} className="btn btn-secondary flex-1">
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isCreatingInvite || (inviteType === 'email' && !inviteEmail)}
                    className="btn btn-primary flex-1"
                  >
                    {isCreatingInvite
                      ? 'Creating...'
                      : inviteType === 'email'
                      ? 'Send Invite'
                      : 'Create Link'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
